import { useEffect, useRef, useState } from 'react';
import type { PresenceState, RealtimeEvent } from '@chromarble/shared-types';
import type { RealtimeStatus } from '../services/realtime';
import { useSession } from '../services/session';
import { serverClock } from '../services/server-clock';

/**
 * Subscribe to a topic for the lifetime of the component. Handlers may change freely.
 * `onSubscribed` runs whenever the subscription becomes active: use it to refetch what may
 * have changed before then (events sent earlier are not delivered).
 */
export function useRealtimeTopic(
  topic: string | null,
  onEvent: (e: RealtimeEvent) => void,
  options: { trackPresence?: boolean; onSubscribed?: () => void } = {},
): PresenceState[] {
  const realtime = useSession((s) => s.realtime);
  const user = useSession((s) => s.user);
  const sessionId = useSession((s) => s.sessionId);
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const subscribed = useRef(options.onSubscribed);
  subscribed.current = options.onSubscribed;
  const [presences, setPresences] = useState<PresenceState[]>([]);

  useEffect(() => {
    if (!realtime || !topic || !user) return;
    const presence: PresenceState | undefined = options.trackPresence
      ? { userId: user.id, nickname: user.nickname, sessionId: sessionId ?? '', onlineAt: Date.now() }
      : undefined;
    return realtime.subscribe(
      topic,
      { onEvent: (e) => handler.current(e), onPresence: setPresences, onSubscribed: () => subscribed.current?.() },
      presence,
    );
  }, [realtime, topic, user, sessionId, options.trackPresence]);

  return presences;
}

export function useRealtimeStatus(): RealtimeStatus {
  const realtime = useSession((s) => s.realtime);
  const [status, setStatus] = useState<RealtimeStatus>(realtime?.status ?? 'closed');
  useEffect(() => {
    if (!realtime) return;
    setStatus(realtime.status);
    return realtime.onStatus(setStatus);
  }, [realtime]);
  return status;
}

/** Browser online/offline flag. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Server-corrected "now", re-rendering every `intervalMs`. */
export function useServerNow(intervalMs = 100): number {
  const [now, setNow] = useState(() => serverClock.now());
  useEffect(() => {
    const t = setInterval(() => setNow(serverClock.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
